import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { ValueSetCode } from '../components/records/ValueSetCombobox';

type CodeSystemPropertyValue = { value: string; description?: string };

/**
 * Structural type for the relevant slice of `medplumService.codeSystem.lookup`'s return
 * shape. Kept local so `@liza/ui` doesn't depend on `@liza/services`.
 */
export interface CodeSystemLookupResponse {
  properties: Record<string, CodeSystemPropertyValue | CodeSystemPropertyValue[]>;
}

interface UseRxNormProductLookupParams<
  TData extends CodeSystemLookupResponse,
  TError,
  TQueryKey extends readonly unknown[],
> {
  /** Factory for the `codeSystem.lookup` trpc query options — must request the
   * `dose_form_of` property for an RxNorm SCD/SBD product so the form can seed
   * the dose-form field from the selected product. */
  queryOptions: () => UseQueryOptions<TData, TError, TData, TQueryKey>;
  /** RxNorm system URL the resolved dose form is tagged with (typically `RXNORM` from
   * `@medplum/core`). The hook is system-agnostic otherwise. */
  rxNormSystem: string;
  /** Whether to actually run the query — usually `Boolean(selectedProduct?.code)`. */
  enabled: boolean;
}

export interface UseRxNormProductLookupResult {
  /** Resolved dose form for the selected product (from `dose_form_of`), or null when
   * the lookup hasn't run or returned nothing. */
  doseForm: ValueSetCode | null;
  /** True while the lookup is in flight — callers thread this to the form's
   * `defaultForm.isLoading` so the dose-form combobox renders its spinner. */
  isFetching: boolean;
  /** True once the lookup has returned successfully. Distinguishes "the terminology answered and
   * carried no `dose_form_of`" from "the lookup hasn't run yet, or failed" — only the former is a
   * data gap worth reporting. */
  isSuccess: boolean;
}

/** RxNorm property values are typically single-valued for an SCD/SBD, but Medplum's
 * terminology service may return them as an array; pick the first entry either way. */
function firstEntry(
  prop: CodeSystemPropertyValue | CodeSystemPropertyValue[] | undefined,
): CodeSystemPropertyValue | undefined {
  return Array.isArray(prop) ? prop.at(0) : prop;
}

export function useRxNormProductLookup<
  TData extends CodeSystemLookupResponse,
  TError = unknown,
  TQueryKey extends readonly unknown[] = readonly unknown[],
>({
  queryOptions,
  rxNormSystem,
  enabled,
}: UseRxNormProductLookupParams<TData, TError, TQueryKey>): UseRxNormProductLookupResult {
  const query = useQuery({ ...queryOptions(), enabled });

  const doseForm = useMemo<ValueSetCode | null>(() => {
    const entry = firstEntry(query.data?.properties.dose_form_of);
    if (!entry) return null;
    return { system: rxNormSystem, code: entry.value, display: entry.description };
  }, [query.data, rxNormSystem]);

  return { doseForm, isFetching: query.isFetching, isSuccess: query.isSuccess };
}
